import React, { useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { CreditCard, Layers, Settings, Users } from 'lucide-react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import AccountSettingsNav from '@/components/AccountSettingsNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Badge } from '@/components/ui/badge'
import HotelChip from '@/components/groups/HotelChip'
import HistoryFilters from '@/components/history/HistoryFilters'
import PersonLabel, { PersonAvatar } from '@/components/history/PersonLabel'
import { DayCard, TimelineEmpty, TimelinePager, TimelineSkeleton, dayDelay, usePendingVisit } from '@/components/history/Timeline'
import { cn } from '@/lib/utils'
import type { ActivityFilters, ActivityItem, ActivityProps } from '@/types/history'

/**
 * BUSINESS ACTIVITY (docs/plans/2026-10-20-change-safety.md §5.13, §5.17.2, R2.9, R2.10): everything
 * changed in this business and who changed it. The pricing history of every hotel, merged with the
 * team, billing and business events, newest first, one card per day.
 *
 * Composed by ResolveBusinessActivityMutation and shown as sent: the day labels, the times (London
 * time), the people (by email), the summaries and every link. A pricing row names its hotel with a
 * link to that hotel's History, a server-built path per row (CLAUDE.md "every deep-linkable row
 * carries its own basePath"). Filters `router.visit` this page's own path, the backend-served
 * settings path plus its fixed suffix; older and newest pages are the server's own hrefs.
 *
 * Owners and admins only (`:view_account_operations`); the server enforces it.
 */
const FILTER_PARAMS: (keyof ActivityFilters)[] = ['hotel', 'person', 'type', 'from', 'to']

// The four kinds of activity, by the value the type filter uses. Unknown kinds fall back to a cog.
const TYPE_ICONS: Record<string, React.ElementType> = {
  pricing: Layers,
  team: Users,
  billing: CreditCard,
  business: Settings,
}

function ActivityRow({ item, typeValue }: { item: ActivityItem; typeValue: string | undefined }) {
  const TypeIcon = (typeValue && TYPE_ICONS[typeValue]) || Settings
  return (
    <li className="flex items-start gap-3 px-5 py-4" data-item={item.id}>
      <span className="hidden w-10 shrink-0 pt-2 text-xs font-medium tabular-nums text-muted-foreground sm:block">
        {item.time}
      </span>
      <PersonAvatar person={item.person} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <PersonLabel person={item.person} className="max-w-full" />
          <span className="text-xs tabular-nums text-muted-foreground sm:hidden">{item.time}</span>
          <Badge
            className={cn(
              'shrink-0 gap-1 border-0 px-2 py-0 text-[11px] font-semibold',
              item.kind === 'pricing'
                ? 'bg-teal-50 text-teal-700 hover:bg-teal-50'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-100'
            )}
          >
            <TypeIcon className="h-3 w-3" aria-hidden="true" />
            {item.typeLabel}
          </Badge>
          {item.hotel && (
            <Link
              href={item.hotel.historyHref}
              className="inline-flex cursor-pointer rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label={`${item.hotel.name}: open its History`}
            >
              <HotelChip name={item.hotel.name} size="sm" className="max-w-full" />
            </Link>
          )}
        </div>
        <p className="mt-1 text-sm font-medium text-foreground [overflow-wrap:anywhere]">{item.summary}</p>
      </div>
    </li>
  )
}

export default function Activity({
  currentUser,
  currentAccount,
  accountAbilities,
  activeSection,
  days,
  nextHref,
  newestHref,
  filters,
  filterOptions,
  emptyState,
}: ActivityProps) {
  const path = `${currentAccount.settingsPath}/activity`
  const { pending, callbacks } = usePendingVisit()
  const listTop = useRef<HTMLDivElement>(null)

  const filtered = FILTER_PARAMS.some((key) => filters[key])
  const showFilters = days.length > 0 || filtered
  // The type filter's own pairs name each row's kind for its icon: a lookup, never a rule.
  const typeValueByLabel = new Map(filterOptions.types.map((option) => [option.label, option.value]))

  function visit(next: ActivityFilters) {
    const data: Record<string, string> = {}
    FILTER_PARAMS.forEach((key) => {
      const value = next[key]
      if (value) data[key] = value
    })
    router.visit(path, { data, preserveState: true, preserveScroll: true, ...callbacks })
  }

  const applyFilters = (updates: Record<string, string | null>) => visit({ ...filters, ...updates })
  const clearFilters = () => visit({ hotel: null, person: null, type: null, from: null, to: null })

  return (
    <PortfolioLayout title="Activity" currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Business settings</h1>
            <HelpTooltip
              content="Every change in this business and who made it: prices on every hotel, the team, billing and settings."
              learnMore="/guides/history"
            />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">Recent changes across this business, newest first.</p>
        </section>

        <AccountSettingsNav accountAbilities={accountAbilities} activeSection={activeSection} settingsPath={currentAccount.settingsPath} />

        {showFilters && (
          <HistoryFilters
            idPrefix="activity"
            className="animate-slide-in"
            style={{ animationDelay: '100ms' }}
            selects={[
              { key: 'hotel', label: 'Hotel', allLabel: 'All hotels', value: filters.hotel, options: filterOptions.hotels },
              { key: 'person', label: 'Person', allLabel: 'Everyone', value: filters.person, options: filterOptions.people },
              { key: 'type', label: 'Type', allLabel: 'All types', value: filters.type, options: filterOptions.types },
            ]}
            from={filters.from}
            to={filters.to}
            active={filtered}
            onChange={applyFilters}
            onClear={clearFilters}
          />
        )}

        <div ref={listTop} className="scroll-mt-24 space-y-4" aria-busy={pending}>
          {pending ? (
            <TimelineSkeleton />
          ) : emptyState ? (
            <TimelineEmpty emptyState={emptyState} onClear={filtered ? clearFilters : undefined} />
          ) : (
            days.map((day, index) => (
              <DayCard key={day.date} label={day.label} date={day.date} delay={dayDelay(index, 150)}>
                {day.items.map((item) => (
                  <ActivityRow key={item.id} item={item} typeValue={typeValueByLabel.get(item.typeLabel)} />
                ))}
              </DayCard>
            ))
          )}
        </div>

        {!pending && (
          <TimelinePager
            nextHref={nextHref}
            newestHref={newestHref}
            olderLabel="Show older activity"
            callbacks={callbacks}
            listTop={listTop}
          />
        )}
      </div>
    </PortfolioLayout>
  )
}
